import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  EyeIcon,
  PlusIcon,
  PrinterIcon,
  SparklesIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome, useHseBusyAction } from "../../../../components/dashboard/hse/HsePageChrome";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import {
  createRiskCampaign,
  deleteRiskCampaign,
  exportRiskRegisterPdf,
  fetchRiskCampaigns,
} from "../../../../api/hse/risk";
import { HseRowActionsMenu } from "../../../../components/dashboard/hse/HseRowActionsMenu";
import { HseGuideButton, HseModuleGuide } from "../../../../components/dashboard/hse/HseModuleGuide";
import { RiskSubnav } from "../../../../components/dashboard/hse/risk/RiskSubnav";
import { HseListCard, HsePageBody } from "../../../../components/dashboard/hse/HsePageBody";
import {
  campaignProgress,
  campaignYearOptions,
  formatDisplayDate,
  RISK_PATHS,
  riskApiError,
} from "../../../../components/dashboard/hse/risk/riskUtils";

const inputClass =
  "mt-2 w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#1463C7]";

function ProgressBar({ campaign }) {
  const { done, total, percent } = campaignProgress(campaign);
  return (
    <div className="min-w-[140px]">
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full ${percent === 100 ? "bg-emerald-500" : "bg-[#1463C7]"}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="mt-1 text-xs font-semibold tabular-nums text-slate-600">
        {done}/{total}
      </p>
    </div>
  );
}

export const HseKsRiskHub = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canCreate = useUserHasAccess({ role: "hse_ks_risk", action: "create" });
  const canEdit = useUserHasAccess({ role: "hse_ks_risk", action: "edit" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ query: "" });
  const [guideOpen, setGuideOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    year: new Date().getFullYear(),
    title: "",
  });
  const { run, isBusy } = useHseBusyAction();

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchRiskCampaigns({
        query: filters.query,
        per_page: 50,
      });
      setItems(response.data || []);
    } catch (err) {
      setError(riskApiError(err) || t("hse.risk.campaign.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query]);

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const saved = await createRiskCampaign({
        year: form.year,
        title: form.title.trim(),
        kind: form.title.trim() ? "custom" : "overall_assessment",
      });
      toast.success(t("hse.risk.campaign.created"));
      setCreateOpen(false);
      navigate(RISK_PATHS.campaign(saved.id));
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.risk.campaign.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t("hse.risk.campaign.delete_confirm", { title: item.title }))) return;
    try {
      await deleteRiskCampaign(item.id);
      toast.success(t("hse.risk.campaign.deleted"));
      load();
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.risk.campaign.save_failed"));
    }
  };

  const handlePdf = (item) =>
    run(`pdf-${item.id}`, async () => {
      try {
        await exportRiskRegisterPdf(
          { hse_risk_campaign_id: item.id },
          `hse-risikovurdering-${item.year || item.id}.pdf`
        );
        toast.success(t("hse.risk.pdf_ready"));
      } catch (err) {
        toast.error(err.message || t("hse.risk.pdf_failed"));
      }
    });

  return (
    <HsePageChrome
      variant="list"
      title={t("hse.risk.campaigns.title")}
      subtitle={t("hse.risk.campaigns.subtitle")}
      listCount={loading ? null : items.length}
      mainButton={
        canCreate
          ? {
              label: t("hse.risk.campaign.new"),
              icon: PlusIcon,
              action: () => setCreateOpen(true),
            }
          : undefined
      }
      actions={[
        {
          label: t("hse.guide.see"),
          icon: undefined,
          action: () => setGuideOpen(true),
          menu: false,
        },
      ]}
    >
      <HsePageBody>
        <RiskSubnav active="campaigns" />

        <div className="mb-4 flex justify-end">
          <HseGuideButton onClick={() => setGuideOpen(true)} />
        </div>

        <ModuleFilter
          filterCard
          hideStatusFilter
          searchPlaceholder={t("hse.risk.campaigns.search")}
          initialFilters={filters}
          onFiltersChange={(next) => setFilters({ query: next.query || "" })}
        />

        <HseListCard>
          {loading ? (
            <div className="p-8">
              <ResourceLoading size="base" />
            </div>
          ) : error ? (
            <ResourceError errors={[error]} onRetry={load} />
          ) : items.length === 0 ? (
            <div className="px-6 py-16">
              <ResourceEmpty message={t("hse.risk.campaigns.empty")} />
              {canCreate ? (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    className="rounded-full bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white"
                  >
                    {t("hse.risk.campaign.new")}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-100 bg-slate-50/80 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">{t("hse.risk.campaigns.col.name")}</th>
                    <th className="px-5 py-3">{t("hse.risk.campaigns.col.year")}</th>
                    <th className="px-5 py-3">{t("hse.risk.campaigns.col.updated")}</th>
                    <th className="px-5 py-3">{t("hse.risk.campaigns.col.progress")}</th>
                    <th className="px-5 py-3">{t("hse.risk.campaigns.col.department")}</th>
                    <th className="px-5 py-3 text-right">{t("hse.risk.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-5 py-4">
                        <Link
                          to={RISK_PATHS.campaign(item.id)}
                          className="font-bold text-[#1463C7] hover:underline"
                        >
                          {item.title}
                        </Link>
                      </td>
                      <td className="px-5 py-4 tabular-nums text-slate-600">{item.year}</td>
                      <td className="px-5 py-4 text-slate-600">{formatDisplayDate(item.updated_at)}</td>
                      <td className="px-5 py-4">
                        <ProgressBar campaign={item} />
                      </td>
                      <td className="px-5 py-4 text-slate-600">
                        {item.department?.displayTitle ||
                          item.department?.title ||
                          t("hse.risk.campaigns.unspecified")}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <HseRowActionsMenu
                          variant="labeled"
                          label={t("hse.risk.more")}
                          header={t("hse.risk.more_header")}
                          items={[
                            {
                              label: t("hse.risk.open"),
                              icon: EyeIcon,
                              action: () => navigate(RISK_PATHS.campaign(item.id)),
                            },
                            {
                              label: t("hse.risk.campaign.add_risk"),
                              icon: PlusIcon,
                              hidden: !canCreate,
                              action: () => navigate(`${RISK_PATHS.campaign(item.id)}?add=1`),
                            },
                            {
                              label: t("hse.risk.add.suggest"),
                              icon: SparklesIcon,
                              hidden: !canCreate,
                              action: () => navigate(`${RISK_PATHS.campaign(item.id)}?suggest=1`),
                            },
                            {
                              label: t("hse.risk.pdf"),
                              icon: PrinterIcon,
                              disabled: isBusy(),
                              loading: isBusy(`pdf-${item.id}`),
                              action: () => handlePdf(item),
                            },
                            {
                              label: t("hse.risk.campaign.delete"),
                              icon: TrashIcon,
                              danger: true,
                              hidden: !canEdit,
                              action: () => handleDelete(item),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </HseListCard>
      </HsePageBody>

      <PMDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("hse.risk.campaign.new")}
        size="lg"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <label className="block">
            <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.campaign.year")}</span>
            <select
              className={inputClass}
              value={form.year}
              onChange={(event) => setForm((current) => ({ ...current, year: Number(event.target.value) }))}
            >
              {campaignYearOptions().map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.campaign.title_optional")}</span>
            <input
              className={inputClass}
              placeholder={t("hse.risk.campaign.title_placeholder", { year: form.year })}
              value={form.title}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
            >
              {t("hse.risk.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? t("hse.risk.saving") : t("hse.risk.save")}
            </button>
          </div>
        </form>
      </PMDialog>

      <HseModuleGuide topic="risk" open={guideOpen} onClose={() => setGuideOpen(false)} />
    </HsePageChrome>
  );
};
